import React, { useState } from "react";
import dynamic from "next/dynamic";
import { Router, useRouter } from "next/router";
import Head from "next/head";
import config from "../../src/config/app.json";
import { apiRequest } from "../api/requestHelper";
import styled from "styled-components";
import Link from "next/link";
import Modal from "react-modal";
import Preview from "../../src/components/Preview";
import useFetchData from "../../src/hooks/useFecthData";
import { getPathName } from "../../src/helpers/utils";
import NewFeatureAlert from "../../src/components/NewFeatureAlert";

const apiUrl = config["APIURIS"][config["APIURIS"]["current"]];

Modal.setAppElement("#__next");
const HeroComponent = dynamic(() => import("../../src/containers/HeroSection"));
const TemplateComponent = dynamic(() =>
  import("../../src/containers/TemplateSection")
);
const BottomComponent = dynamic(() =>
  import("../../src/containers/BottomSection")
);

const TemplatePageContainer = styled.div`
  height: 100%;
`;

const PaginationContainer = styled.div`
  display: flex;
  width: 100%;
  justify-content: center;
  align-items: center;
  box-sizing: border-box;
  padding: 50px 0px;

  @media (max-width: 500px) {
    padding: 25px 0px;
  }
`;

const PageNumberLink = styled.a`
  width: 40px;
  height: 40px;
  display: flex;
  color: #eee;
  justify-content: center;
  align-items: center;
  text-decoration: none;
  border-radius: 50%;
  color: #2d2d2d;
  margin-right: 10px;
  font-family: "Lato";
  font-size: 14px;
  &:hover {
    color: ${(props) => (props.active ? "#fff" : "#f67062")};
  }
  ${(props) =>
    props.active &&
    `background: linear-gradient(45deg,#f67062,#fc5296);
    color: #fff;
    `}
  @media(max-width: 425px) {
    margin-right: 0px;
  }
`;

const PageNavLink = styled(PageNumberLink)`
  border: 2px solid #efefef;
  font-size: 18px;
`;

const PageContinue = styled.span`
  display: flex;
  color: #eee;
  justify-content: center;
  align-items: center;
  text-decoration: none;
  border-radius: 50%;
  color: #2d2d2d;
  margin-right: 10px;
  @media (max-width: 425px) {
    margin-right: 0px;
  }
`;

const PaginationWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;

  @media (max-width: 500px) {
    flex-direction: column;
  }
`;

const PageInputContainer = styled.form`
  display: flex;
  align-items: center;
  position: absolute;
  right: 35px;
  p {
    color: #2d2d2d;
    margin-right: 10px;
    font-family: "Lato";
    font-size: 14px;
  }
  input {
    width: 35px;
    height: 35px;
    border-radius: 4px;
    border: 1px solid #424158;
    display: flex;
    justify-content: center;
    align-items: center;
    margin: 10px;
    outline: 0;
    color: #2d2d2d;
    margin-right: 10px;
    font-family: "Lato";
    font-size: 14px;
    text-align: center;
  }

  /* Chrome, Safari, Edge, Opera */
  input::-webkit-outer-spin-button,
  input::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  /* Firefox
    input[type="number"] {
      -moz-appearance: textfield;
    } */

  @media (max-width: 500px) {
    padding: 15px 0px;
    position: unset;
    right: unset;
  }
`;

const MessageContainer = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
  margin-top: 20px;
  padding: 40px 10px 0px;
  box-sizing: border-box;
  p {
    margin: 0;
    font-family: "Lato";
    font-size: 16px;
    text-align: center;
    color: #54536e;
    line-height: 1.4;
    font-weight: bold;
  }
  @media (max-width: 500px) {
    margin-top: 15px;
    padding: 15px 10px 0px;
  }
`;

const PAGE_LIMIT = 30;
const appUrl = config["TEMPLATEURLS"][config["TEMPLATEURLS"]["current"]];
const appConfigUrl = config["APPURLS"][config["APPURLS"]["current"]];

function SectionPage({ data, isSearchPage, toShowMessage }) {
  const router = useRouter();
  const [activePage, setActivePage] = useState(null);
  const { seo_data, templates, trackId, category_details } = data
    ? data
    : { seo_data: [], templates: [], trackId: "", category_details: [] };
  const { modalData } = useFetchData();

  const categoryId = seo_data[0]["_source"]?.categoryId || "";
  const categoryDetails = !!category_details
    ? category_details.filter((item) => item["_source"].category === categoryId)
    : [];
  const categoryName =
    categoryDetails.length > 0 ? categoryDetails[0]["_source"].name : "";
  const pageList = Array.from(Array(getTotalPageNumber()).keys());
  const {
    title,
    meta_description,
    keywords,
    h1_tag,
    tags,
    byline,
    banner_img,
    og_image,
  } = seo_data[0]["_source"]
    ? seo_data[0]["_source"]
    : {
        title: "Templates | Picmaker",
        description: "Templates List",
        keyword: "Templates",
        banner_img: "",
        byline: "",
        og_image: "",
      };

  const [showFeatureAlert, setShowFeatureAlert] = useState(true);

  function handleCloseFeatureAlert() {
    setShowFeatureAlert(false);
  }

  function getTitle() {
    if (Object.keys(modalData).length > 0) {
      return modalData.design.name_en;
    }
    return router.query && router.query.continuation
      ? `Page ${
          Math.round(router.query.continuation / PAGE_LIMIT) + 1
        } - ${title}`
      : title;
  }

  function getActivePage() {
    const currentPageNumber =
      router.query && router.query.continuation
        ? Math.round(router.query.continuation / PAGE_LIMIT) + 1
        : 1;
    if (activePage === null) {
      setActivePage(currentPageNumber);
    }

    return currentPageNumber;
  }

  function getTotalPageNumber() {
    return Math.ceil(templates.total / PAGE_LIMIT);
  }

  function getPageLink(page, continuation) {
    const { query } = router;
    if (!isSearchPage) {
      if (page === 0) {
        return query.tagId ? `/${query.id}/${query.tagId}` : `/${query.id}`;
      }
      return query.tagId
        ? `/${query.id}/${query.tagId}?continuation=${continuation}`
        : `/${query.id}?continuation=${continuation}`;
    }
    return page === 0
      ? `${router.pathname}/?id=${router.query.id}`
      : `${router.pathname}/?id=${router.query.id}&continuation=${continuation}`;
  }
  function getNavigationLink(type) {
    if (type === "backward") {
      return getActivePage() === 2
        ? getPageLink(0, "")
        : getPageLink(getActivePage(), router.query.continuation - PAGE_LIMIT);
    } else if (type === "forward") {
      return getPageLink(getActivePage(), getActivePage() * PAGE_LIMIT);
    }
  }

  function getPaginationControls() {
    const paginationControls = pageList.map((page, index) => {
      const continuation = PAGE_LIMIT * page;
      const currentPage = page + 1;
      return (
        <Link
          href={getPageLink(page, continuation)}
          passHref
          key={`Page ${currentPage}`}
          scroll
        >
          <PageNumberLink
            id={`Page ${currentPage}`}
            key={index}
            active={getActivePage() === currentPage}
            onClick={() => {
              window.scrollTo(0, 0);
            }}
          >
            {currentPage}
          </PageNumberLink>
        </Link>
      );
    });

    return getRenderPaginationControls(paginationControls);
  }

  function getRenderPaginationControls(paginationControls) {
    const leftOffsetLimit =
      getActivePage() - 2 > 0 && getActivePage() !== pageList.length
        ? getActivePage() - 2
        : getActivePage() === pageList.length
        ? getActivePage() - 3
        : 1;
    const rightOffsetLimit =
      getActivePage() === 1 ? getActivePage() + 2 : getActivePage() + 1;
    if (pageList.length > 6) {
      return [
        paginationControls[0],
        getActivePage() - 3 > 0 && (
          <PageContinue key="backward-continue">...</PageContinue>
        ),
        ...paginationControls.slice(leftOffsetLimit, rightOffsetLimit),
        getActivePage() + 2 < pageList.length && (
          <PageContinue key="forward-continue">...</PageContinue>
        ),
        getActivePage() + 2 <= pageList.length &&
          paginationControls[paginationControls.length - 1],
      ];
    }
    return paginationControls;
  }

  function getCloseLink() {
    return router.query.continuation
      ? `/${router.query.id}?continuation=${router.query.continuation}`
      : `/${router.query.id}`;
  }

  function getPageScripts() {
    const id = router.query.id;
    const name = getPathName(id);
    return JSON.stringify([
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        name: "Breadcrumb",
        itemListElement: [
          {
            "@type": "ListItem",
            position: 1,
            name: "Home",
            item: "https://www.picmaker.com/",
          },
          {
            "@type": "ListItem",
            position: 2,
            name: "Templates",
            item: "https://www.picmaker.com/templates",
          },
          {
            "@type": "ListItem",
            position: 3,
            name: `${name}`,
            item: `https://www.picmaker.com/templates/${id}`,
          },
        ],
      },
    ]);
  }

  function handleInputChange(event) {
    setActivePage(event.target.value);
  }

  function handleSubmit(event) {
    event.preventDefault();
    if (activePage) {
      const continuation = PAGE_LIMIT * (activePage - 1);
      const pageLink = getPageLink(activePage - 1, continuation);
      window.scroll(0, 0);
      router.push(pageLink, undefined, { shallow: false, scroll: true });
    }
  }

  return (
    <>
      <Head>
        <title>{getTitle()}</title>
        <meta property="og:title" content={getTitle()} />
        {og_image && (
          <>
            <meta
              property="og:image"
              content={`${appConfigUrl}landing/og/${og_image}`}
            />
            <meta
              property="og:image:url"
              content={`${appConfigUrl}landing/og/${og_image}`}
            />
            <meta property="og:image:width" content={"140"} />
            <meta property="og:image:height" content={"140"} />
            <meta property="og:image:type" content={"image/png"} />
            <meta
              property="og:url"
              content={`${appConfigUrl}landing/og/${og_image}`}
            />
            <meta property="og:description" content={meta_description} />
          </>
        )}
        <meta name="keywords" content={keywords} />
        <meta name="description" content={meta_description} />
        <link rel="canonical" href={`${appUrl}/templates${router.asPath}`} />
        {getActivePage() === 1 && pageList.length > 0 && (
          <>
            <link
              rel="next"
              href={`${appUrl}/templates${getNavigationLink("forward")}`}
            />
          </>
        )}
        {getActivePage() !== 1 && (
          <>
            <link
              rel="prev"
              href={`${appUrl}/templates${getNavigationLink("backward")}`}
            />
            {getActivePage() !== pageList.length && (
              <link
                rel="next"
                href={`${appUrl}/templates${getNavigationLink("forward")}`}
              />
            )}
          </>
        )}
        {!router.query.tagId && (
          <script type="application/ld+json">{getPageScripts()}</script>
        )}
      </Head>
      <TemplatePageContainer id="template-page-container">
        <HeroComponent
          title={h1_tag}
          byline={byline}
          banner_img={banner_img}
          toShowMessage={toShowMessage}
        />
        {toShowMessage &&
          (isSearchPage ? (
            <MessageContainer>
              <p>{`Sorry, we couldn't find any templates for "${h1_tag}". But here are some of our most popular templates you might like.`}</p>
            </MessageContainer>
          ) : (
            <MessageContainer>
              <p>{`Sorry, we couldn't find any templates for "${h1_tag}" Please check back in a few days. Meanwhile, here are some of our most popular templates you might like.`}</p>
            </MessageContainer>
          ))}
        <TemplateComponent
          data={templates["templates"]}
          tags={[...new Set([...tags.map((t) => t.toLowerCase())])]}
          categoryId={categoryId}
          categoryName={categoryName}
          isSearchPage={isSearchPage}
          toShowMessage={toShowMessage}
        />
        {pageList && pageList.length !== 1 && (
          <PaginationWrapper key="pagination-wrapper">
            <PaginationContainer key="pagination-conatiner">
              <>
                {getActivePage() !== 1 && (
                  <Link href={getNavigationLink("backward")} passHref scroll>
                    <PageNavLink
                      onClick={() => {
                        window.scrollTo(0, 0);
                      }}
                    >
                      {"<"}
                    </PageNavLink>
                  </Link>
                )}
                {getPaginationControls()}
                {pageList.length > 0 &&
                  getTotalPageNumber() !== getActivePage() && (
                    <Link href={getNavigationLink("forward")} passHref scroll>
                      <PageNavLink
                        onClick={() => {
                          window.scrollTo(0, 0);
                        }}
                      >
                        {">"}
                      </PageNavLink>
                    </Link>
                  )}
              </>
            </PaginationContainer>
            <PageInputContainer onSubmit={handleSubmit}>
              <input
                type="number"
                min="1"
                max={pageList.length}
                value={activePage}
                required
                onChange={handleInputChange}
              />
              <span>
                <p>{`of ${pageList.length} pages`} </p>
              </span>
            </PageInputContainer>
          </PaginationWrapper>
        )}
        <BottomComponent trackId={trackId} />
        <Modal
          isOpen={!!router.query.templateId}
          onRequestClose={() => {
            router.push(getCloseLink(), undefined, { shallow: true });
          }}
          className="modal"
          overlayClassName="overlay"
        >
          <Preview
            data={modalData}
            allData={router.query.origin === "preview" ? [] : data}
            closeLink={getCloseLink()}
          />
        </Modal>
      </TemplatePageContainer>
      {/* {showFeatureAlert && (
        <NewFeatureAlert handleCloseFeatureAlert={handleCloseFeatureAlert} />
      )} */}
    </>
  );
}

export default React.memo(SectionPage);

export async function getServerSideProps(context) {
  const { params, query } = context;
  const { id } = params;
  let tag = "";

  const fetchQuery = query.continuation
    ? () => {
        return {
          method: "post",
          url: `${apiUrl}dynamic/templates/`,
          options: {
            category_url: query.id,
            from: query.continuation || 0,
            size: 30,
            tags: query.tagId ? [query.tagId] : [],
          },
        };
      }
    : () => {
        return {
          method: "post",
          url: `${apiUrl}dynamic/templates/`,
          options: {
            category_url: query.id,
            from: 0,
            size: 30,
            tags: [],
          },
        };
      };

  const { data } = await apiRequest(
    fetchQuery().method,
    fetchQuery().url,
    fetchQuery().options
  );

  if (!data["data"]["all_templates"]) {
    if (
      Object.keys(data["data"]).length <= 0 ||
      data["data"].seo_data.length <= 0 ||
      Object.keys(data["data"].templates).length <= 0
    ) {
      return {
        notFound: true,
      };
    }
  }

  const tempData = {
    seo_data: data["data"]["seo_data"],
    templates:
      Object.keys(data["data"]["templates"]).length > 0
        ? data["data"]["templates"]
        : data["data"]["all_templates"],
    trackId: data["data"]["trackid"],
    category_details: data["data"]["design_category"],
  };
  const toShowMessage = data["data"]["all_templates"] ? true : false;

  return {
    props: {
      data: tempData,
      tag: tag,
      toShowMessage,
    },
  };
}
